/**
 * Custom wishlist implementation.
 * Shopify has no native wishlist object, so this stores product handles in
 * localStorage (client-side only — it will not sync across devices). Swap
 * this file out if a wishlist app is installed later.
 */
(function () {
  const STORAGE_KEY = 'tarangini:wishlist';

  function getHandles() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  }

  function setHandles(handles) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    } catch (e) {
      /* localStorage unavailable (private mode, etc.) — fail silently */
    }
    window.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { handles } }));
  }

  function has(handle) {
    return getHandles().includes(handle);
  }

  function toggle(handle) {
    const handles = getHandles();
    const index = handles.indexOf(handle);
    if (index > -1) {
      handles.splice(index, 1);
    } else {
      handles.unshift(handle);
    }
    setHandles(handles);
    return index === -1;
  }

  function remove(handle) {
    setHandles(getHandles().filter((h) => h !== handle));
  }

  window.Tarangini = window.Tarangini || {};
  window.Tarangini.wishlist = { getHandles, has, toggle, remove };

  function updateToggleButtons() {
    document.querySelectorAll('.wishlist-toggle[data-product-handle]').forEach((button) => {
      const active = has(button.getAttribute('data-product-handle'));
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      const label = button.querySelector('[data-wishlist-label]');
      if (label) {
        label.textContent = active ? label.dataset.labelSaved : label.dataset.labelDefault;
      }
    });
  }

  function updateCountBubble() {
    const count = getHandles().length;
    document.querySelectorAll('.wishlist-count-bubble').forEach((bubble) => {
      bubble.textContent = count > 99 ? '99+' : String(count);
      bubble.hidden = count === 0;
    });
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('.wishlist-toggle');
    if (!button) return;
    event.preventDefault();
    const handle = button.getAttribute('data-product-handle');
    if (!handle) return;
    toggle(handle);
  });

  window.addEventListener('wishlist:updated', () => {
    updateToggleButtons();
    updateCountBubble();
    const drawer = document.querySelector('wishlist-drawer');
    if (drawer && drawer.classList.contains('active')) drawer.render();
  });

  document.addEventListener('DOMContentLoaded', () => {
    updateToggleButtons();
    updateCountBubble();

    const icon = document.querySelector('#wishlist-icon-bubble');
    const drawer = document.querySelector('wishlist-drawer');
    if (icon && drawer) {
      icon.setAttribute('role', 'button');
      icon.setAttribute('aria-haspopup', 'dialog');
      icon.addEventListener('click', (event) => {
        event.preventDefault();
        drawer.open(icon);
      });
    }
  });

  class WishlistDrawer extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
      const overlay = this.querySelector('#WishlistDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      const closeButtons = this.querySelectorAll('.drawer__close');
      closeButtons.forEach((btn) => btn.addEventListener('click', this.close.bind(this)));

      this.addEventListener('click', (event) => {
        const removeBtn = event.target.closest('[data-wishlist-remove]');
        if (removeBtn) {
          const item = removeBtn.closest('[data-wishlist-item]');
          remove(item.dataset.handle);
          this.render();
          return;
        }
        const addBtn = event.target.closest('[data-wishlist-add]');
        if (addBtn) {
          const item = addBtn.closest('[data-wishlist-item]');
          this.addToCart(item.dataset.variantId, addBtn);
        }
      });
    }

    open(triggeredBy) {
      if (triggeredBy) this.activeElement = triggeredBy;
      this.render();
      document.body.classList.add('overflow-hidden');
      setTimeout(() => this.classList.add('animate', 'active'));
    }

    close() {
      this.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
      if (this.activeElement) this.activeElement.focus();
    }

    async render() {
      const handles = getHandles();
      const itemsContainer = this.querySelector('#WishlistDrawer-Items');
      const emptyState = this.querySelector('#WishlistDrawer-Empty');
      if (!itemsContainer) return;

      if (handles.length === 0) {
        itemsContainer.innerHTML = '';
        if (emptyState) emptyState.hidden = false;
        return;
      }
      if (emptyState) emptyState.hidden = true;

      const template = document.querySelector('#WishlistItemTemplate');
      itemsContainer.innerHTML = '';

      const products = await Promise.all(
        handles.map((handle) =>
          fetch(`/products/${handle}.js`)
            .then((res) => (res.ok ? res.json() : null))
            .catch(() => null)
        )
      );

      products.forEach((product, index) => {
        if (!product || !template) return;
        const handle = handles[index];
        const node = template.content.cloneNode(true);
        const root = node.querySelector('[data-wishlist-item]');
        root.dataset.handle = handle;
        root.dataset.variantId = product.variants[0].id;

        node.querySelectorAll('[data-wishlist-link]').forEach((el) => (el.href = `/products/${handle}`));
        const image = node.querySelector('[data-wishlist-image]');
        if (image && product.featured_image) {
          image.src = product.featured_image;
          image.alt = product.title;
        }
        const title = node.querySelector('[data-wishlist-title]');
        if (title) title.textContent = product.title;
        const price = node.querySelector('[data-wishlist-price]');
        if (price) price.textContent = this.formatMoney(product.price);

        itemsContainer.appendChild(node);
      });
    }

    formatMoney(cents) {
      if (window.Shopify && Shopify.formatMoney && window.themeMoneyFormat) {
        return Shopify.formatMoney(cents, window.themeMoneyFormat);
      }
      return `₹${(cents / 100).toFixed(2)}`;
    }

    async addToCart(variantId, button) {
      button.disabled = true;
      try {
        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });
        if (response.ok) {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) {
            fetch(`${window.routes.cart_url}?section_id=cart-drawer`)
              .then((res) => res.text())
              .then((text) => {
                const html = new DOMParser().parseFromString(text, 'text/html');
                const newInner = html.querySelector('#CartDrawer');
                const currentInner = document.querySelector('#CartDrawer');
                if (newInner && currentInner) currentInner.innerHTML = newInner.innerHTML;
                this.close();
                cartDrawer.open();
              });
          }
        }
      } finally {
        button.disabled = false;
      }
    }
  }

  if (!customElements.get('wishlist-drawer')) {
    customElements.define('wishlist-drawer', WishlistDrawer);
  }
})();
